import { Geofence } from '@eagle/core-data-types';
import {
  CacheDataTypes,
  evaluate,
  EventLocationData,
  Icon,
  TRACK_TRIP_SUMMARY_DEFAULT_TEMPLATE,
  TRACK_TRIP_TIMELINE_ITEM_DEFAULT_TEMPLATE,
  TRACK_TRIP_TIMELINE_MARKER_DEFAULT_TEMPLATE,
  useAuthenticated,
  useFetchOneCache,
  useHistorySearch,
  useUiTemplate,
} from '@eagle/react-common';
import { Box, Button, Divider, Skeleton, Stack, Switch, Tooltip, Typography } from '@ethings/ui-toolkit';
import { DateTime } from 'luxon';
import { FC, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  filterExcludedTripHistoryEvents,
  getEventColor,
  getEventDisplayLabel,
  getTripEventsInRange,
  getTripTimelineProcessedEvents,
  isGeofenceEnterOrLeaveEventType,
  isLocationUpdateEvent,
  isVisibleTripTimelineEvent,
  stripTrailingStatusDot,
  toEventTime,
} from '../trip-history-base-view-utils';
import { TripDrawerContentProps, TripDrawerTimelineItem } from '../trip-history-base-view.types';
import { isSameTripJourneyMapItem, isTripHistoryMapItem } from '../trip-history-map-item-utils';
import { fetchAllThingEvents } from '../trip-history-paginated-events';
import { createThrottledReverseGeocodeResolver, formatAddressText, resolveTimelineAddressText } from '../trip-history-route-address-utils';
import { enrichEventsWithNearestLocationUpdate } from '../trip-history-timeline-processing-utils';
import { TripDrawerTimeline } from './trip-drawer-timeline';

const TRIP_SUMMARY_TEMPLATE_ID = 'track-trip-summary';
const TRIP_TIMELINE_ITEM_TEMPLATE_ID = 'track-trip-timeline-item';
const TRIP_TIMELINE_MARKER_TEMPLATE_ID = 'track-trip-timeline-marker';

export const TripDrawerContent: FC<TripDrawerContentProps> = ({ eventDefinitions, hoveredEventId, onHoverEvent, onSelectEvent, reverseGeocodeEnabled, selectedEventId, selectedTrip, selectedTripMetricEvents, selectedTripRouteLabel, selectedThingColor, thingColorById, tripEventSummarizationOptions, tripLocationEnrichmentOptions }) => {
  const { t } = useTranslation(['terms', 'common', 'track']);
  const { restClient } = useAuthenticated();
  const geofenceCache = useFetchOneCache(CacheDataTypes.GEOFENCE);
  const { eventData, setClickedEventId, setEventData, setShowLocationUpdateEvents, showLocationUpdateEvents } = useHistorySearch();
  const [geofenceDisplayById, setGeofenceDisplayById] = useState<Record<string, string>>({});
  const [tripTimelineAddressLabelByEventId, setTripTimelineAddressLabelByEventId] = useState<Record<string, string>>({});
  const [tripTimelineAddressLoadingByEventId, setTripTimelineAddressLoadingByEventId] = useState<Record<string, boolean>>({});
  const [visibleTripTimelineItemIds, setVisibleTripTimelineItemIds] = useState<string[]>([]);
  const reverseGeocodeLabelByCoordinateRef = useRef<Map<string, string>>(new Map());
  const reverseGeocodeInFlightByCoordinateRef = useRef<Map<string, Promise<string>>>(new Map());
  const thingColor = selectedTrip ? (thingColorById[selectedTrip.entityId] ?? selectedThingColor) : selectedThingColor;
  const unknownLocationLabel = t('common:terms.unknown');
  const showLocationUpdates = selectedTrip ? (showLocationUpdateEvents[selectedTrip.id] ?? false) : false;
  const { template: tripSummaryTemplate, loaded: tripSummaryTemplateLoaded } = useUiTemplate(
    TRIP_SUMMARY_TEMPLATE_ID,
    TRACK_TRIP_SUMMARY_DEFAULT_TEMPLATE,
  );
  const { template: tripTimelineItemTemplate, loaded: tripTimelineItemTemplateLoaded } = useUiTemplate(
    TRIP_TIMELINE_ITEM_TEMPLATE_ID,
    TRACK_TRIP_TIMELINE_ITEM_DEFAULT_TEMPLATE,
  );
  const { template: tripTimelineMarkerTemplate, loaded: tripTimelineMarkerTemplateLoaded } = useUiTemplate(
    TRIP_TIMELINE_MARKER_TEMPLATE_ID,
    TRACK_TRIP_TIMELINE_MARKER_DEFAULT_TEMPLATE,
  );

  useEffect(() => {
    if (!selectedTrip) return;

    const normalizeTripItems = (items: EventLocationData[]): EventLocationData[] => {
      const inRangeItems = filterExcludedTripHistoryEvents(getTripEventsInRange(selectedTrip, items));
      const enrichedItems = enrichEventsWithNearestLocationUpdate(inRangeItems, tripLocationEnrichmentOptions);
      return enrichedItems.filter((event) => isLocationUpdateEvent(event) || isVisibleTripTimelineEvent(event, eventDefinitions));
    };

    const toLocationKey = (event: EventLocationData): string => {
      const location = event.data?.location;
      if (!location || !Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) return '';
      return `${location.latitude}:${location.longitude}`;
    };

    const tripEventData = eventData.find((item) => {
      return isTripHistoryMapItem(item, selectedTrip);
    });

    if (tripEventData) {
      const currentTripItems = Array.isArray(tripEventData.data.items) ? tripEventData.data.items as EventLocationData[] : [];
      const normalizedTripItems = normalizeTripItems(currentTripItems);
      const hasItemsChanged = currentTripItems.length !== normalizedTripItems.length
        || currentTripItems.some((event, index) => {
          const normalizedEvent = normalizedTripItems[index];
          if (!normalizedEvent) return true;
          return event.eventId !== normalizedEvent.eventId || toLocationKey(event) !== toLocationKey(normalizedEvent);
        });

      if (
        tripEventData.isSelected
        && tripEventData.isOngoing === selectedTrip.isOngoing
        && tripEventData.showLocationUpdates === showLocationUpdates
        && !hasItemsChanged
      ) {
        return;
      }

      setEventData(eventData
        .filter((item) => !isSameTripJourneyMapItem(item, selectedTrip) || isTripHistoryMapItem(item, selectedTrip))
        .map((item) => {
          if (!isTripHistoryMapItem(item, selectedTrip)) {
            return item;
          }

          return {
            ...item,
            data: {
              ...item.data,
              count: normalizedTripItems.length,
              items: normalizedTripItems,
            },
            forceVisible: true,
            isOngoing: selectedTrip.isOngoing,
            isSelected: true,
            showLocationUpdates,
          };
        }));
      return;
    }

    let isCancelled = false;
    const dateRangeStart = selectedTrip.start.toJSDate();
    const dateRangeFinish = (selectedTrip.isOngoing ? DateTime.now() : selectedTrip.finish).toJSDate();

    fetchAllThingEvents<EventLocationData>(
      (thingId, requestParams) => restClient.thingEvent.getWithThing(thingId, requestParams as never),
      selectedTrip.entityId,
      {
        dateRangeStart,
        dateRangeFinish,
        sort: 'occurred',
      },
    ).then((response) => {
      if (isCancelled) return;
      const resolvedSelectedTripItems = normalizeTripItems(response.items);

      const updatedEventData = eventData
        .map((item) => ({
          ...item,
          forceVisible: isTripHistoryMapItem(item, selectedTrip) ? true : item.forceVisible,
          isOngoing: isTripHistoryMapItem(item, selectedTrip) ? selectedTrip.isOngoing : item.isOngoing,
          isSelected: isTripHistoryMapItem(item, selectedTrip) ? true : item.isSelected,
          showLocationUpdates: isTripHistoryMapItem(item, selectedTrip) ? showLocationUpdates : item.showLocationUpdates,
        }))
        .filter((item) => !isSameTripJourneyMapItem(item, selectedTrip));

      updatedEventData.push({
        color: thingColor,
        data: {
          ...response,
          count: resolvedSelectedTripItems.length,
          items: resolvedSelectedTripItems,
        },
        entityDisplay: selectedTrip.entityId,
        entityId: selectedTrip.entityId,
        focused: true,
        forceVisible: true,
        isOngoing: selectedTrip.isOngoing,
        isSelected: true,
        journeyId: selectedTrip.journeyId,
        showLocationUpdates,
        tripId: selectedTrip.id,
      });

      setEventData(updatedEventData);
    }).catch(() => {
      // Leave existing timeline state unchanged when mock fetch fails.
    });

    return () => {
      isCancelled = true;
    };
  }, [eventData, eventDefinitions, restClient.thingEvent, selectedTrip, setEventData, showLocationUpdates, thingColor, tripLocationEnrichmentOptions]);

  const isInProgress = selectedTrip?.isOngoing ?? false;

  const tripEventData = selectedTrip
    ? eventData.find((item) => isTripHistoryMapItem(item, selectedTrip))
    : undefined;
  const rawTripEventItems = useMemo<EventLocationData[]>(() => {
    const items = tripEventData?.data.items;
    return Array.isArray(items) ? items as EventLocationData[] : [];
  }, [tripEventData?.data.items]);
  const resolvedTripEventItems = useMemo(() => {
    return enrichEventsWithNearestLocationUpdate(rawTripEventItems, tripLocationEnrichmentOptions);
  }, [rawTripEventItems, tripLocationEnrichmentOptions]);
  const selectedTripEnd = selectedTrip ? (selectedTrip.isOngoing ? DateTime.now() : selectedTrip.finish) : undefined;
  const isTimelineEventVisible = useCallback((event: EventLocationData): boolean => {
    return isVisibleTripTimelineEvent(event, eventDefinitions);
  }, [eventDefinitions]);

  const allTripEvents = useMemo<EventLocationData[]>(() => {
    if (!selectedTrip || !selectedTripEnd) return [];

    const filteredTripEvents = filterExcludedTripHistoryEvents(resolvedTripEventItems)
      .filter((event) => isTimelineEventVisible(event));

    return [...filteredTripEvents]
      .filter((event) => {
        const eventTime = toEventTime(event.occurred);
        return eventTime >= selectedTrip.start && eventTime <= selectedTripEnd;
      })
      .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());
  }, [isTimelineEventVisible, resolvedTripEventItems, selectedTrip, selectedTripEnd]);

  const startEvent = allTripEvents[0] as EventLocationData | undefined;
  const endEvent = allTripEvents.length ? allTripEvents[allTripEvents.length - 1] : undefined;
  const timelineEvents = allTripEvents
    .filter((event) => showLocationUpdates || event.eventTypeId !== 'location-update');
  const processedTimelineEvents = useMemo(() => {
    return getTripTimelineProcessedEvents(timelineEvents, tripEventSummarizationOptions);
  }, [timelineEvents, tripEventSummarizationOptions]);
  const reverseGeocodeApi = useMemo(() => (
    restClient.reverseGeocode as unknown as {
      get: (params: { lat: number; long: number }) => Promise<{ address?: unknown }>;
    }
  ), [restClient.reverseGeocode]);
  const throttledReverseGeocode = useMemo(() => {
    return createThrottledReverseGeocodeResolver((params) => reverseGeocodeApi.get(params), { intervalMs: 250 });
  }, [reverseGeocodeApi]);

  const resolvedTripTimelineAddressLabelByEventId = useMemo<Record<string, string>>(
    () => tripTimelineAddressLabelByEventId ?? {},
    [tripTimelineAddressLabelByEventId],
  );

  useEffect(() => {
    setTripTimelineAddressLabelByEventId((current) => (Object.keys(current).length ? {} : current));
    setTripTimelineAddressLoadingByEventId((current) => (Object.keys(current).length ? {} : current));
    setVisibleTripTimelineItemIds((current) => (current.length ? [] : current));
  }, [selectedTrip?.id]);

  useEffect(() => {
    if (reverseGeocodeEnabled) return;
    setTripTimelineAddressLabelByEventId((current) => (Object.keys(current).length ? {} : current));
    setTripTimelineAddressLoadingByEventId((current) => (Object.keys(current).length ? {} : current));
  }, [reverseGeocodeEnabled]);

  const visibleTripTimelineEvents = useMemo(() => {
    const visibleItemIds = new Set(visibleTripTimelineItemIds);
    const eventsById = new Map<string, EventLocationData>();

    if (selectedTrip && startEvent && visibleItemIds.has(`${selectedTrip.id}-start`)) {
      eventsById.set(startEvent.eventId, startEvent);
    }

    processedTimelineEvents.forEach((entry) => {
      const isSummarized = entry.isSummarized && entry.eventCount && entry.eventCount > 1;
      if (!isSummarized && visibleItemIds.has(entry.event.eventId)) {
        eventsById.set(entry.event.eventId, entry.event);
      }
    });

    if (selectedTrip && endEvent && !isInProgress && visibleItemIds.has(`${selectedTrip.id}-end`)) {
      eventsById.set(endEvent.eventId, endEvent);
    }

    return Array.from(eventsById.values());
  }, [endEvent, isInProgress, processedTimelineEvents, selectedTrip, startEvent, visibleTripTimelineItemIds]);

  useEffect(() => {
    if (!reverseGeocodeEnabled || !visibleTripTimelineEvents.length) {
      setTripTimelineAddressLoadingByEventId((current) => (Object.keys(current).length ? {} : current));
      return;
    }

    const missingEvents = visibleTripTimelineEvents.filter((event) => !tripTimelineAddressLabelByEventId[event.eventId]);
    if (!missingEvents.length) return;

    let isCancelled = false;
    const reverseGeocodeTimeoutMs = 8000;
    const missingEventIds = missingEvents.map((event) => event.eventId);

    const withTimeout = async <T,>(promise: Promise<T>): Promise<T> => {
      let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
      try {
        return await Promise.race([
          promise,
          new Promise<T>((_, reject) => {
            timeoutId = globalThis.setTimeout(() => {
              reject(new Error('Reverse geocode timed out'));
            }, reverseGeocodeTimeoutMs);
          }),
        ]);
      } finally {
        if (timeoutId !== undefined) {
          globalThis.clearTimeout(timeoutId);
        }
      }
    };

    setTripTimelineAddressLoadingByEventId((current) => {
      const next = { ...current };
      missingEvents.forEach((event) => {
        next[event.eventId] = true;
      });
      return next;
    });

    missingEvents.forEach((event) => {
      void (async () => {
        try {
          const addressLabel = await withTimeout(resolveTimelineAddressText({
            cache: reverseGeocodeLabelByCoordinateRef.current,
            enabled: reverseGeocodeEnabled,
            event,
            inFlight: reverseGeocodeInFlightByCoordinateRef.current,
            reverseGeocode: throttledReverseGeocode,
            unknownLabel: unknownLocationLabel,
          }));
          if (isCancelled) return;
          setTripTimelineAddressLabelByEventId((current) => ({
            ...current,
            [event.eventId]: addressLabel,
          }));
        } catch {
          if (isCancelled) return;
          setTripTimelineAddressLabelByEventId((current) => ({
            ...current,
            [event.eventId]: formatAddressText(event, unknownLocationLabel),
          }));
        } finally {
          if (!isCancelled) {
            setTripTimelineAddressLoadingByEventId((current) => {
              const next = { ...current };
              delete next[event.eventId];
              return next;
            });
          }
        }
      })();
    });

    return () => {
      isCancelled = true;
      setTripTimelineAddressLoadingByEventId((current) => {
        const next = { ...current };
        missingEventIds.forEach((eventId) => {
          delete next[eventId];
        });
        return next;
      });
    };
  }, [
    reverseGeocodeEnabled,
    throttledReverseGeocode,
    tripTimelineAddressLabelByEventId,
    unknownLocationLabel,
    visibleTripTimelineEvents,
  ]);

  const geofenceIds = useMemo(() => {
    return Array.from(new Set(processedTimelineEvents
      .map((entry) => entry.event)
      .filter((event) => isGeofenceEnterOrLeaveEventType(event.eventTypeId))
      .map((event) => (event.data as { geofenceId?: string } | undefined)?.geofenceId)
      .filter((geofenceId): geofenceId is string => Boolean(geofenceId))));
  }, [processedTimelineEvents]);

  useEffect(() => {
    if (!geofenceIds.length) return;

    const missingGeofenceIds = geofenceIds.filter((geofenceId) => !geofenceDisplayById[geofenceId]);
    if (!missingGeofenceIds.length) return;

    let isCancelled = false;

    void Promise.all(missingGeofenceIds.map(async (geofenceId) => {
      const geofence = await geofenceCache.one<Geofence>(geofenceId).catch(() => undefined);
      return { geofenceDisplay: geofence?.display ?? geofenceId, geofenceId };
    })).then((entries) => {
      if (isCancelled) return;

      setGeofenceDisplayById((current) => {
        const next = { ...current };
        entries.forEach(({ geofenceDisplay, geofenceId }) => {
          next[geofenceId] = geofenceDisplay;
        });
        return next;
      });
    }).catch(() => {
      // Keep timeline rendering even when geofence name lookup fails.
    });

    return () => {
      isCancelled = true;
    };
  }, [geofenceCache, geofenceDisplayById, geofenceIds]);

  const formatTimelineEventSubtitle = useCallback((event: EventLocationData): JSX.Element | string => {
    const addressLabel = resolvedTripTimelineAddressLabelByEventId[event.eventId] ?? formatAddressText(event, unknownLocationLabel);
    if (!isGeofenceEnterOrLeaveEventType(event.eventTypeId)) return addressLabel;

    const geofenceId = (event.data as { geofenceId?: string } | undefined)?.geofenceId;
    const geofenceDisplayRaw = geofenceId ? (geofenceDisplayById[geofenceId] ?? geofenceId) : t('track:page.history.trip-base-view.labels.unknown-geofence');
    const geofenceDisplay = stripTrailingStatusDot(geofenceDisplayRaw);

    return (
      <Stack spacing={0.25}>
        <Stack alignItems="center" color="text.secondary" direction="row" paddingBottom={0.5} spacing={0.5}>
          <Icon color="inherit" fontSize="extra-small" icon="GeofenceIcon" />
          <Typography lineHeight={1} variant="caption">{geofenceDisplay}</Typography>
        </Stack>
        <Typography color="text.secondary" variant="caption">{addressLabel}</Typography>
      </Stack>
    );
  }, [geofenceDisplayById, t, resolvedTripTimelineAddressLabelByEventId, unknownLocationLabel]);
  const isTripTimelineAddressLabelLoading = useCallback((eventId?: string): boolean => {
    return eventId ? tripTimelineAddressLoadingByEventId[eventId] === true : false;
  }, [tripTimelineAddressLoadingByEventId]);

  if (!selectedTrip) {
    return <></>;
  }

  const timelineItems: TripDrawerTimelineItem[] = [
    {
      color: thingColor,
      id: `${selectedTrip.id}-start`,
      label: t('common:terms.start'),
      subtitle: isTripTimelineAddressLabelLoading(startEvent?.eventId)
        ? <Skeleton data-testid="history-trip-events-subtitle-skeleton" height={16} variant="rounded" width={140} />
        : (startEvent
          ? (resolvedTripTimelineAddressLabelByEventId[startEvent.eventId] ?? formatAddressText(startEvent, unknownLocationLabel))
          : formatAddressText(startEvent, unknownLocationLabel)),
      time: toEventTime(startEvent?.occurred ?? selectedTrip.start.toISO()).toFormat('hh:mm a'),
      type: 'start',
    },
    ...processedTimelineEvents.map((entry) => {
      const event = entry.event;
      const originalEvents = entry.originalEvents ?? [];
      const sortedOriginalEvents = [...originalEvents]
        .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());
      const firstOccurred = sortedOriginalEvents[0];
      const lastOccurred = sortedOriginalEvents[sortedOriginalEvents.length - 1];
      const isSummarized = entry.isSummarized && entry.eventCount && entry.eventCount > 1;
      const timeLabel = isSummarized && firstOccurred && lastOccurred
        ? `${toEventTime(firstOccurred.occurred).toFormat('hh:mm a')} - ${toEventTime(lastOccurred.occurred).toFormat('hh:mm a')}`
        : toEventTime(event.occurred).toFormat('hh:mm a');

      return {
        color: getEventColor(event, eventDefinitions),
        event: isSummarized
          ? {
            ...event,
            _eventCount: entry.eventCount,
            _isSummarized: true,
            _originalEvents: originalEvents,
            _summarizationMode: entry.summarizationMode,
          } as EventLocationData
          : event,
        id: event.eventId,
        label: getEventDisplayLabel(event, eventDefinitions),
        subtitle: isSummarized
          ? `${entry.eventCount ?? 0} Occurrences`
          : (isTripTimelineAddressLabelLoading(event.eventId)
            ? <Skeleton data-testid="history-trip-events-subtitle-skeleton" height={16} variant="rounded" width={140} />
            : formatTimelineEventSubtitle(event)),
        time: timeLabel,
        type: 'event' as const,
      };
    }),
    {
      color: thingColor,
      id: `${selectedTrip.id}-${isInProgress ? 'ongoing' : 'end'}`,
      label: isInProgress ? t('track:page.history.ongoing.labels') : t('track:page.history.trip-base-view.labels.end'),
      subtitle: isInProgress
        ? t('track:page.history.trip-base-view.labels.trip-in-progress')
        : (isTripTimelineAddressLabelLoading(endEvent?.eventId)
          ? <Skeleton data-testid="history-trip-events-subtitle-skeleton" height={16} variant="rounded" width={140} />
          : (endEvent
            ? (resolvedTripTimelineAddressLabelByEventId[endEvent.eventId] ?? formatAddressText(endEvent, unknownLocationLabel))
            : formatAddressText(endEvent, unknownLocationLabel))),
      time: toEventTime(endEvent?.occurred ?? selectedTrip.finish.toISO()).toFormat('hh:mm a'),
      type: isInProgress ? 'ongoing' : 'end',
    },
  ];

  const tripLocationSubtitle = selectedTripRouteLabel;
  const tripSummaryTemplateContext = {
    'thing-activity': {
      entityId: selectedTrip.entityId,
      finish: selectedTrip.finish.toISO(),
      id: selectedTrip.id,
      isOngoing: selectedTrip.isOngoing,
      journeyId: selectedTrip.journeyId,
      start: selectedTrip.start.toISO(),
    },
    durationMinutes: Math.max(Math.round(selectedTrip.finish.diff(selectedTrip.start, 'minutes').minutes), 0),
    events: {
      distancePrimaryIncrease: selectedTripMetricEvents?.distancePrimaryIncreaseEvent,
      distancePrimaryStart: selectedTripMetricEvents?.odometerStartEvent,
      distancePrimaryEnd: selectedTripMetricEvents?.odometerEndEvent,
      engineRunningStart: selectedTripMetricEvents?.engineHoursStartEvent,
      engineRunningEnd: selectedTripMetricEvents?.engineHoursEndEvent,
      fuelConsumed: selectedTripMetricEvents?.fuelConsumedEvent,
    },
  };

  return (
    <Box p={2} style={{ boxSizing: 'border-box', height: '100%' }}>
      <Stack gap={1.75} style={{ height: '100%', minHeight: 0 }}>
        <Stack alignItems="flex-start" direction="row" spacing={1.25}>
          <Box style={{ color: selectedThingColor, flexShrink: 0, marginTop: 2 }}>
            <Icon fontSize="small" icon="ThingIcon" />
          </Box>
          <Stack spacing={0.25} style={{ minWidth: 0 }}>
            <Typography variant="body2">
              <Tooltip placement="bottom" title={selectedTrip.start.toFormat('dd MMM yyyy, hh:mm a')}>
                <span>{selectedTrip.start.toFormat('hh:mm a')}</span>
              </Tooltip>
              {' - '}
              <Tooltip placement="bottom" title={isInProgress ? t('track:page.history.trip-base-view.labels.current') : selectedTrip.finish.toFormat('dd MMM yyyy, hh:mm a')}>
                <span>{isInProgress ? t('track:page.history.trip-base-view.labels.current') : selectedTrip.finish.toFormat('hh:mm a')}</span>
              </Tooltip>
            </Typography>
            <Typography color="text.secondary" style={{ wordBreak: 'break-word' }} variant="caption">
              {tripLocationSubtitle}
            </Typography>
          </Stack>
        </Stack>

        {tripSummaryTemplateLoaded && <>{evaluate(tripSummaryTemplate, tripSummaryTemplateContext)}</>}

        <Divider />

        <Button
          color="primary"
          size="small"
          data-testid="trip-detail-location-updates-button"
          onClick={() => {
            setShowLocationUpdateEvents((current) => ({
              ...current,
              [selectedTrip.id]: !showLocationUpdates,
            }));
          }}
          style={{ alignSelf: 'flex-start' }}
          variant={'light' as 'text'}
        >
          <Stack alignItems="center" direction="row" spacing={1}>
            <Switch
              checked={showLocationUpdates}
              data-testid="trip-detail-location-updates-toggle"
              onClick={(event) => event.stopPropagation()}
              onChange={(_event, checked) => {
                setShowLocationUpdateEvents((current) => ({
                  ...current,
                  [selectedTrip.id]: checked,
                }));
              }}
              size="small"
            />
            <Typography lineHeight="2.5" variant="caption">{t('track:page.history.trip-base-view.labels.location-updates')}</Typography>
          </Stack>
        </Button>

        <Box style={{ flex: 1, minHeight: 0 }}>
          <TripDrawerTimeline
            hoveredEventId={hoveredEventId}
            itemTemplate={tripTimelineItemTemplate}
            itemTemplateLoaded={tripTimelineItemTemplateLoaded}
            items={timelineItems}
            markerTemplate={tripTimelineMarkerTemplate}
            markerTemplateLoaded={tripTimelineMarkerTemplateLoaded}
            onHoverEvent={onHoverEvent}
            onVisibleItemIdsChange={setVisibleTripTimelineItemIds}
            onSelectEvent={(event) => {
              setClickedEventId(event.eventId);
              onSelectEvent(event);
            }}
            selectedEventId={selectedEventId}
          />
        </Box>
      </Stack>
    </Box>
  );
};
